import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CartProvider, useCart } from "@/contexts/CartContext";
import CartDrawer from "@/components/CartDrawer";
import StatusBadge from "@/components/StatusBadge";
import StarRating from "@/components/StarRating";
import WishlistButton from "@/components/WishlistButton";
import ReviewSection from "@/components/ReviewSection";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useI18n } from "@/contexts/I18nContext";
import ProductConfigurator from "@/components/ProductConfigurator";
import VisualConfigurator from "@/components/VisualConfigurator";
import BrandStyle from "@/components/BrandStyle";
import { resolveUrl } from "@/components/ImageUploader";
import BrandLogo from "@/components/BrandLogo";
import { ArrowLeft, ShoppingBag, Check, Loader2, X, Share2, Facebook, Twitter, Linkedin, Mail } from "lucide-react";
import { toast } from "sonner";

export default function StorefrontProductWrapper() {
  const { slug } = useParams();
  return (
    <CartProvider slug={slug}>
      <StorefrontProduct />
      <CartDrawer />
    </CartProvider>
  );
}

function StorefrontProduct() {
  const { slug, sku } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const cart = useCart();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reviewsSummary, setReviewsSummary] = useState({ average: 0, count: 0 });
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [imageOverride, setImageOverride] = useState(null);
  const [liveComposition, setLiveComposition] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/storefront/${slug}/product/${sku}`).then((r) => {
      setData(r.data);
      setActiveImage(0);
      setImageOverride(null);
    });
  }, [slug, sku]);

  useEffect(() => {
    if (data?.product?.sku_madre) {
      axios.get(`${API}/public/reviews/${data.product.sku_madre}?tenant_slug=${slug}`).then((r) => setReviewsSummary({ average: r.data.average, count: r.data.count })).catch(() => {});
    }
  }, [data, slug]);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const { tenant, network, product } = data;
  const statuses = network.vertical_config?.availability_statuses || [];
  const statusConfig = statuses.find((s) => s.key === product.stato_disponibilita);
  const brand = tenant.settings?.brand_kit || {};
  const primaryColor = brand.secondary_color || "#C25934";

  const images = imageOverride && imageOverride.length ? imageOverride : (product.immagini && product.immagini.length ? product.immagini : []);
  const video = product.video_url;

  const shareUrl = window.location.href;
  const shareText = encodeURIComponent(`Guarda "${product.nome}" su ${tenant.name}`);
  const shareLinks = [
    { net: "facebook", icon: Facebook, url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { net: "x", icon: Twitter, url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${shareText}` },
    { net: "linkedin", icon: Linkedin, url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
    { net: "whatsapp", icon: Share2, url: `https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}` },
    { net: "email", icon: Mail, url: `mailto:?subject=${shareText}&body=${encodeURIComponent(shareUrl)}` },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="storefront-product" data-storefront="true">
      <BrandStyle brand={brand} />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to={`/s/${slug}`} data-testid="product-back" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Indietro
          </Link>
          <BrandLogo logoUrl={brand.logo_url} fallbackText={tenant.name} resolveUrl={resolveUrl} className="h-8" dataTestid="product-brand-logo" />
          <LanguageSwitcher />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Gallery */}
          <div>
            <div className="aspect-square overflow-hidden bg-neutral-100 mb-3 cursor-zoom-in" data-testid="product-main-image" onClick={() => setLightbox(true)}>
              {images[activeImage] && <img src={resolveUrl(images[activeImage])} alt={product.nome} className="w-full h-full object-cover" />}
            </div>
            <div className="grid grid-cols-5 gap-2">
              {images.slice(0, 5).map((img, i) => (
                <button key={i} data-testid={`thumb-${i}`} onClick={() => setActiveImage(i)}
                  className={`aspect-square overflow-hidden bg-neutral-100 border ${i === activeImage ? "border-brand" : "border-transparent"}`}>
                  <img src={resolveUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
              {video && (
                <button data-testid="thumb-video" onClick={() => setActiveImage(-1)}
                  className="aspect-square overflow-hidden bg-black border border-transparent flex items-center justify-center text-white text-xs">
                  ▶ Video
                </button>
              )}
            </div>
            {activeImage === -1 && video && (
              <div className="mt-3 aspect-video bg-black">
                <video src={resolveUrl(video)} controls className="w-full h-full" />
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="overline text-brand mb-3">{product.categoria}</div>
            <h1 className="editorial-h1 text-4xl lg:text-5xl mb-2" data-testid="product-name">{product.nome}</h1>
            {reviewsSummary.count > 0 && (
              <div className="flex items-center gap-2 mb-3" data-testid="product-rating-summary">
                <StarRating value={reviewsSummary.average} />
                <span className="text-xs text-muted-foreground">{reviewsSummary.average} · {reviewsSummary.count} {t("store_reviews").toLowerCase()}</span>
              </div>
            )}
            <p className="text-lg text-muted-foreground mb-6">{product.descrizione_short}</p>
            <div className="flex items-baseline gap-3 mb-6">
              <span className="font-heading font-bold text-4xl">€ {product.prezzo_finale}</span>
              {product.sconto_percentuale > 0 && (
                <>
                  <span className="text-lg text-muted-foreground line-through">€ {product.prezzo_originale}</span>
                  <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-brand text-white">-{product.sconto_percentuale}%</span>
                </>
              )}
            </div>
            <div className="mb-6">
              <StatusBadge status={product.stato_disponibilita} statuses={statuses} />
              {statusConfig?.eta_text && <span className="ml-3 text-sm text-muted-foreground">{statusConfig.eta_text}</span>}
            </div>

            {/* Stock visibility */}
            <div className="mb-6 p-3 rounded-sm border border-border bg-neutral-50/50 flex items-center gap-3" data-testid="product-stock-info">
              {product.stock_disponibile > 0 ? (
                <>
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  <div className="text-sm">
                    <span className="font-medium text-emerald-700">{product.stock_disponibile}</span>
                    <span className="text-muted-foreground"> {product.stock_disponibile === 1 ? t("store_in_stock_one") : t("store_in_stock_many")} · {t("store_in_stock")}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <div className="text-sm">
                    <span className="font-medium text-amber-700">{t("store_preorder")}</span>
                    {product.lead_time_gg > 0 && <span className="text-muted-foreground"> · {t("store_preorder_days", { n: product.lead_time_gg })}</span>}
                  </div>
                </>
              )}
            </div>
            {product.descrizione_long && <p className="text-sm text-muted-foreground leading-relaxed mb-8 whitespace-pre-line">{product.descrizione_long}</p>}

            {product.variant_axes?.length > 0 && (
              <ProductConfigurator
                slug={slug}
                axes={product.variant_axes}
                variants={product.variants_all || []}
                currentSku={product.sku_variante}
                primaryColor={primaryColor}
                onChange={(newSku) => navigate(`/s/${slug}/product/${newSku}`, { replace: true })}
                onImageOverride={(imgs) => { setImageOverride(imgs); setActiveImage(0); }}
              />
            )}

            {/* Configuratore visuale "personalizza tu" (Step 3) */}
            {product.configurator_enabled && product.configurator_options && (
              <div className="mb-8">
                <VisualConfigurator
                  prodotto={product}
                  options={product.configurator_options}
                  prezzoBase={product.prezzo_base}
                  productImage={images[0]}
                  accentColor={primaryColor}
                  onComposition={setLiveComposition}
                />
              </div>
            )}

            <div className="border-t border-border pt-6 mb-8">
              <div className="overline text-brand mb-3">{t("store_specs")}</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {Object.entries(product.custom_attributes_variante || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-border py-1.5">
                    <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                    <span className="font-medium">{v}</span>
                  </div>
                ))}
                {product.garanzia_mesi > 0 && <Spec label={t("store_warranty")} value={`${product.garanzia_mesi} ${t("store_months")}`} />}
                {product.lead_time_gg > 0 && <Spec label={t("store_lead_time")} value={`${product.lead_time_gg} ${t("store_days")}`} />}
              </div>
            </div>

            <div className="flex items-stretch gap-3 mb-6">
              <div className="flex items-center border border-border rounded-sm">
                <button data-testid="qty-decrement" onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-12">−</button>
                <span className="w-12 text-center font-medium" data-testid="qty-display">{qty}</span>
                <button data-testid="qty-increment" onClick={() => setQty(qty + 1)} className="w-11 h-12">+</button>
              </div>
              <button data-testid="add-cart-btn" disabled={product.stato_disponibilita === "non_disponibile"}
                onClick={() => { cart.add(product.sku_variante, qty, { nome: product.nome, image: product.immagini?.[0], custom_attributes: product.custom_attributes_variante }); }}
                className="flex-1 h-12 rounded-sm text-white text-sm font-medium inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
                style={{ background: primaryColor }}>
                <ShoppingBag className="w-4 h-4" /> {t("store_add_cart")}
              </button>
              <WishlistButton sku={product.sku_variante} tenantSlug={slug} />
            </div>

            <Link to={`/s/${slug}/checkout`} data-testid="buy-now-link"
              onClick={() => { cart.add(product.sku_variante, qty, { nome: product.nome, image: product.immagini?.[0], custom_attributes: product.custom_attributes_variante }); }}
              className="w-full h-11 mb-6 rounded-sm border border-foreground text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-foreground hover:text-white transition-colors">
              <Check className="w-4 h-4" /> {t("store_buy_now")} · € {(product.prezzo_finale * qty).toFixed(2)}
            </Link>

            {/* Social share */}
            <div className="border-t border-border pt-6">
              <div className="overline text-muted-foreground mb-3">{t("store_share")}</div>
              <div className="flex gap-2">
                {shareLinks.map(({ net, icon: Icon, url }) => (
                  <a key={net} href={url} target="_blank" rel="noreferrer" data-testid={`share-${net}`}
                    className="w-10 h-10 rounded-sm border border-border flex items-center justify-center hover:bg-accent hover:border-brand">
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <ReviewSection skuMadre={product.sku_madre} tenantSlug={slug} />
      </div>

      {/* Lightbox */}
      {lightbox && images[activeImage] && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(false)} data-testid="lightbox">
          <button className="absolute top-6 right-6 text-white w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-testid="lightbox-close" onClick={() => setLightbox(false)}>
            <X className="w-5 h-5" />
          </button>
          <img src={resolveUrl(images[activeImage])} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}

const Spec = ({ label, value }) => (
  <div className="flex justify-between border-b border-border py-1.5">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);
